import type { IChallenge } from "@bbj-types/challenge";
import type {} from "@bbj-types/utils";
import { StackGrid, stackGridClasses } from "@data-display/layout/StackGrid";
import { yupResolver } from "@hookform/resolvers/yup";
import { IconButtonVariant } from "@inputs/button/IconButtonVariant";
import { AutocompleteForm, DateInputForm, TextFieldForm } from "@inputs/hook-form";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress, Divider, Stack } from "@mui/material";
import { useI18nContext } from "@root/utils/organisation";
import { useLayoutEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import type { TPaperCandidateChallengeObjective } from "../types";
import { type IFormCandidateObjective, type IResolverCandidateObjective, formatDefautValues, resolver } from "./logics";
import { paperCandidateObjectiveClasses } from "./styles";

enum LoadingState {
  NONE = 0,
  SUBMIT = 1,
  CANCEL = 2,
}

export type IPaperCandidateObjectiveFormProps<
  T extends TPaperCandidateChallengeObjective = TPaperCandidateChallengeObjective,
> = {
  disabled?: boolean;
  open?: boolean;
  challenge: Pick<IChallenge, "objectives">;
  candidateObjective?: T;
  onSubmit: (candidateObjective: T) => void | Promise<void>;
  onCancel: () => void | Promise<void>;
};

export const PaperCandidateObjectiveForm = <
  T extends TPaperCandidateChallengeObjective = TPaperCandidateChallengeObjective,
>({
  disabled,
  open,
  challenge,
  candidateObjective,
  onSubmit,
  onCancel,
}: IPaperCandidateObjectiveFormProps<T>) => {
  const [loading, setLoading] = useState(LoadingState.NONE);
  const { timeZone } = useI18nContext();
  const { control, handleSubmit, reset } = useForm<IFormCandidateObjective, void, IResolverCandidateObjective>({
    defaultValues: formatDefautValues(challenge, candidateObjective),
    // biome-ignore lint/suspicious/noExplicitAny: YupResolver type issue
    resolver: yupResolver(resolver(timeZone)) as any,
  });

  const activeObjectives = useMemo(() => {
    return challenge.objectives.filter((obj) => obj.active);
  }, [challenge.objectives]);

  const submit = async (data: IResolverCandidateObjective) => {
    setLoading(LoadingState.SUBMIT);
    try {
      const baseCandidateObjective = {
        objective: data.objective._id,
        ...(data.objectiveEndDate && { deadline: data.objectiveEndDate }),
        ...(data.objectiveTitle && { label: data.objectiveTitle }),
      } satisfies TPaperCandidateChallengeObjective;
      if (candidateObjective === undefined) {
        await onSubmit(baseCandidateObjective as T);
      } else {
        await onSubmit({ ...candidateObjective, ...baseCandidateObjective });
      }
    } finally {
      setLoading(LoadingState.NONE);
    }
  };

  const cancel = async () => {
    setLoading(LoadingState.CANCEL);
    try {
      await onCancel();
    } finally {
      setLoading(LoadingState.NONE);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Reset form on open or candidateObjective update
  useLayoutEffect(() => {
    if (!open) {
      reset(formatDefautValues(challenge, candidateObjective));
    }
  }, [open, candidateObjective]);

  return (
    <Stack className={paperCandidateObjectiveClasses.form}>
      <Divider flexItem orientation="horizontal" />
      <StackGrid perLine={{ tablet: 2 }} gap={2}>
        <AutocompleteForm
          control={control}
          name="objective"
          options={activeObjectives}
          getOptionKey={(option) => option._id}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          label="Type d'objectif *"
          size="small"
          disabled={Boolean(candidateObjective?._id)}
          fullWidth
        />
        <DateInputForm
          control={control}
          name="objectiveEndDate"
          label="Date butoir"
          TextFieldProps={{ size: "small", fullWidth: true }}
          disablePast
        />
        <TextFieldForm
          control={control}
          name="objectiveTitle"
          maxLength={80}
          label="Titre"
          size="small"
          fullWidth
          className={stackGridClasses.fullWidth}
        />
      </StackGrid>
      <Stack className={paperCandidateObjectiveClasses.formActions}>
        <IconButtonVariant
          type="button"
          variant="elevated"
          size="large"
          onClick={cancel}
          disabled={disabled || loading !== LoadingState.NONE}
        >
          {loading === LoadingState.CANCEL ? (
            <CircularProgress color="primary" size={20} />
          ) : (
            <CloseIcon color="primary" />
          )}
        </IconButtonVariant>
        <IconButtonVariant
          type="button"
          variant="elevated"
          size="large"
          onClick={handleSubmit(submit)}
          disabled={disabled || loading !== LoadingState.NONE}
        >
          {loading === LoadingState.SUBMIT ? (
            <CircularProgress color="primary" size={20} />
          ) : (
            <CheckIcon color="success" />
          )}
        </IconButtonVariant>
      </Stack>
    </Stack>
  );
};
